import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, TruckIcon, ClipboardListIcon } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useCart } from '../../context/CartContext';
import { useAchievement } from '../../context/AchievementContext';
const OrderConfirmation: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    clearCart
  } = useCart();
  const {
    addPoints
  } = useAchievement();
  // Clear the cart and add points when order is confirmed
  useEffect(() => {
    clearCart();
    addPoints(50); // Award 50 points for placing an order
  }, [clearCart, addPoints]);
  return <div className="max-w-3xl mx-auto" data-id="element-724">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-id="element-725">
        <div className="text-center mb-6 sm:mb-8" data-id="element-726">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-727">
            <CheckCircleIcon size={32} className="text-green-500" data-id="element-728" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold mb-2" data-id="element-729">
            Order Confirmed!
          </h1>
          <p className="text-gray-600" data-id="element-730">
            Your order #{id} has been placed successfully
          </p>
        </div>
        {/* Order details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 sm:mb-8" data-id="element-731">
          <h2 className="font-semibold mb-3 text-base sm:text-lg" data-id="element-732">
            Order Details
          </h2>
          <div className="space-y-2 text-sm sm:text-base" data-id="element-733">
            <div className="flex justify-between" data-id="element-734">
              <span className="text-gray-600" data-id="element-735">Order Number:</span>
              <span className="font-medium" data-id="element-736">{id}</span>
            </div>
            <div className="flex justify-between" data-id="element-737">
              <span className="text-gray-600" data-id="element-738">Date:</span>
              <span className="font-medium" data-id="element-739">
                {new Date().toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-740">
              <span className="text-gray-600" data-id="element-741">Estimated Delivery:</span>
              <span className="font-medium" data-id="element-742">12:30 PM - 1:00 PM</span>
            </div>
            <div className="flex justify-between" data-id="element-743">
              <span className="text-gray-600" data-id="element-744">Payment Method:</span>
              <span className="font-medium" data-id="element-745">Credit Card</span>
            </div>
          </div>
        </div>
        {/* Delivery status */}
        <div className="border border-gray-200 rounded-lg p-4 mb-6 sm:mb-8" data-id="element-746">
          <div className="flex items-center mb-4" data-id="element-747">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 sm:mr-4" data-id="element-748">
              <TruckIcon size={20} className="text-blue-600" data-id="element-749" />
            </div>
            <div data-id="element-750">
              <h2 className="font-semibold text-base sm:text-lg" data-id="element-751">
                Delivery Status
              </h2>
              <p className="text-sm text-gray-600" data-id="element-752">
                You can track your order in real-time
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-753">
            <div className="flex items-center mb-4" data-id="element-754">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center z-10" data-id="element-755">
                <CheckCircleIcon size={16} className="text-white" data-id="element-756" />
              </div>
              <div className="ml-4" data-id="element-757">
                <p className="font-medium" data-id="element-758">Order Received</p>
                <p className="text-xs sm:text-sm text-gray-500" data-id="element-759">
                  {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="absolute top-0 left-4 w-0.5 h-full bg-gray-200 -z-10" data-id="element-760"></div>
            <div className="flex items-center" data-id="element-761">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center z-10" data-id="element-762">
                <span className="text-gray-500 text-sm" data-id="element-763">2</span>
              </div>
              <div className="ml-4" data-id="element-764">
                <p className="font-medium text-gray-500" data-id="element-765">
                  Preparing Your Order
                </p>
                <p className="text-xs sm:text-sm text-gray-500" data-id="element-766">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
        {/* Points earned */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 sm:mb-8" data-id="element-767">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-768">
            <div className="mb-2 sm:mb-0" data-id="element-769">
              <h3 className="font-semibold text-yellow-800" data-id="element-770">
                You earned 50 points!
              </h3>
              <p className="text-xs sm:text-sm text-yellow-700" data-id="element-771">
                Keep ordering to level up your Food Passport
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/food-passport')} data-id="element-772">
              View Food Passport
            </Button>
          </div>
        </div>
        <div className="space-y-3 sm:space-y-4" data-id="element-773">
          <Button onClick={() => navigate(`/order/tracking/${id}`)} fullWidth size="lg" data-id="element-774">
            Track Order
          </Button>
          <Button variant="outline" onClick={() => navigate('/order/history')} fullWidth className="flex items-center justify-center" data-id="element-775">
            <ClipboardListIcon size={18} className="mr-2" data-id="element-776" />
            View Order History
          </Button>
        </div>
      </div>
    </div>;
};
export default OrderConfirmation;